import type { AutoTeamLead, AutoTeamRoutine, RoleRecipeId } from "@rakazo/contracts";
import { buildSkillMd } from "./agent-skill.js";
import { WEBSITE_UPKEEP_SKILL } from "./website-upkeep.js";

/**
 * Department role recipes for the team Chief proposes. Each is a generic starting point: what the
 * lead owns, the routines it suggests, a how-to skill and the approvals it keeps. Chief adapts the
 * names, prompts and schedules to the business; nothing here names an industry. Approving the
 * team creates each lead's routines paused, so nothing runs until the owner switches it on.
 */
export interface RoleRecipe {
  id: RoleRecipeId;
  /** The usual sidebar section. */
  department: string;
  /** A starting title Chief adapts. */
  title: string;
  /** When a business needs this lead, for Chief's choice. */
  when: string;
  duties: readonly string[];
  routines: readonly AutoTeamRoutine[];
  /** The how-to skill the lead reads; the website recipe uses the built-in Website upkeep. */
  howTo: { name: string; description: string; body: string };
  /** What the lead always asks the owner before doing. */
  approvals: readonly string[];
  /** Standing rules, such as when to hand a conversation to a person. */
  rules: readonly string[];
}

export const ROLE_RECIPES: readonly RoleRecipe[] = [
  {
    id: "support",
    department: "Support",
    title: "Support Lead",
    when: "Customers email or message the business with questions, orders or complaints.",
    duties: [
      "Triage the support inbox: sort each message by topic and urgency.",
      "Draft replies from the FAQ, memory and earlier answers, in the company's tone.",
      "Keep the FAQ up to date with new questions and the answers the owner approved.",
    ],
    routines: [
      {
        name: "Inbox triage",
        prompt:
          "Read new support messages since the last run. Sort them by urgency, draft replies from the FAQ and memory for review, and list anything that needs a person.",
        cron: "0 9,14 * * 1-5",
      },
      {
        name: "FAQ upkeep",
        prompt:
          "Review this week's support questions. Propose new or corrected FAQ entries for questions asked more than once, with the approved answers.",
        cron: "0 16 * * 5",
      },
      {
        name: "Support summary",
        prompt:
          "Summarise today's support: messages in, replies drafted, open conversations, and anything waiting on the owner.",
        cron: "30 17 * * 1-5",
      },
    ],
    howTo: {
      name: "Support desk how-to",
      description: "Triage messages, draft replies from the FAQ, and hand off what needs a person.",
      body: `1. Read the message and the customer's history in memory.
2. Sort it: question, order or account, complaint, or other. Mark it urgent when the customer is waiting on something time-bound.
3. Find the answer in the FAQ skill, memory or an earlier approved reply. Never invent policies, prices or promises.
4. Draft the reply in the company's tone: answer first, then the next step. Keep it short.
5. Hand off instead of drafting when a handoff rule applies, with a one-line summary of why.
6. Record new questions for the weekly FAQ upkeep.`,
    },
    approvals: ["Ask before sending any reply to a customer."],
    rules: [
      "Hand the conversation to the owner, with a short summary, when a customer is angry, the request is unusual, or the customer or order is high value.",
    ],
  },
  {
    id: "finance",
    department: "Finance",
    title: "Finance Lead",
    when: "The business sends invoices, pays suppliers or tracks expenses.",
    duties: [
      "Read invoices and receipts from PDFs, photos and scans, and record the supplier, date, amounts and tax.",
      "Keep the expense sheet up to date.",
      "Track money owed to the business and chase it politely.",
    ],
    routines: [
      {
        name: "Receipts and invoices",
        prompt:
          "Read invoices and receipts received since the last run (PDFs, photos and scans). Add each to the expense sheet with supplier, date, net, tax and total, and list any that could not be read.",
        cron: "0 10 * * 1-5",
      },
      {
        name: "Credit control",
        prompt:
          "List unpaid invoices by how overdue they are. Draft a polite payment reminder for each one past its due date, for approval before sending.",
        cron: "0 9 * * 1",
      },
    ],
    howTo: {
      name: "Finance desk how-to",
      description: "Read invoices from PDFs and scans, keep the expense sheet, chase payments.",
      body: `1. Open each invoice or receipt. For a scan or photo, read it with the file tools and check the totals add up.
2. Record supplier, invoice number, date, due date, net, tax, total and currency. Mark any field you could not read instead of guessing.
3. Flag duplicates and amounts that differ from the order or contract.
4. For credit control, sort unpaid invoices by days overdue and draft reminders: friendly at first, firmer after 30 days.
5. Never send money, approve a payment, or change bank details.`,
    },
    approvals: [
      "Ask before sending any payment reminder.",
      "Never send money, approve a payment or change bank details.",
    ],
    rules: [],
  },
  {
    id: "executive_assistant",
    department: "Office",
    title: "Executive Assistant",
    when: "The owner has a busy inbox and calendar and wants a daily plan.",
    duties: [
      "Send a morning briefing: what needs the owner, today's meetings, and overdue open work.",
      "Prepare for meetings with the people, history and open points.",
      "Write meeting notes with decisions and actions, and track the actions.",
    ],
    routines: [
      {
        name: "Morning briefing",
        prompt:
          "Brief the owner for today: items that need them first, then today's meetings with one line of context each, then overdue open work.",
        cron: "30 7 * * 1-5",
      },
      {
        name: "Meeting prep",
        prompt:
          "For tomorrow's meetings, prepare a short note each: who is attending, the history from memory and email, and the open points.",
        cron: "0 16 * * 1-5",
      },
    ],
    howTo: {
      name: "Assistant how-to",
      description: "Morning briefings, meeting prep and meeting notes with actions.",
      body: `1. Briefing order: needs-you items first, then today's meetings, then overdue open work. One line each.
2. Meeting prep: attendees, what they want, the history from memory and email, and the open points.
3. Meeting notes: decisions, actions with an owner and a due date, and open questions. Add each action to open work.
4. Never accept, decline or move a meeting, or reply for the owner, without asking.`,
    },
    approvals: ["Ask before accepting, declining or moving a meeting, or replying for the owner."],
    rules: [],
  },
  {
    id: "operations",
    department: "Operations",
    title: "Operations Lead",
    when: "The business enters data in supplier or government portals, or depends on vendors.",
    duties: [
      "Log into supplier and government portals and enter or download data.",
      "Write a daily operations report: what is late, at risk, or needs a decision.",
      "Follow up with vendors on late orders and open questions.",
    ],
    routines: [
      {
        name: "Daily operations report",
        prompt:
          "Write today's operations report: what is late, what is at risk, what needs a decision, and what was done.",
        cron: "0 17 * * 1-5",
      },
      {
        name: "Vendor follow-ups",
        prompt:
          "List vendor orders and questions that are late or unanswered. Draft a short follow-up for each, for approval before sending.",
        cron: "0 10 * * 2,4",
      },
    ],
    howTo: {
      name: "Operations how-to",
      description: "Portal data entry with handover for logins, reports and vendor follow-ups.",
      body: `1. Portals: open the site in the browser with the saved login. When it asks for a password you do not have, a code or a check you cannot pass, hand over with request_takeover and continue once the owner is done.
2. Before submitting a form, check every field against the source and keep a copy of what was entered.
3. Daily report: late, at risk, needs a decision, done. One line each.
4. Vendor follow-ups: quote the order or question, say what is needed and by when.`,
    },
    approvals: [
      "Ask before submitting a form to a government portal.",
      "Ask before sending a vendor follow-up.",
    ],
    rules: [],
  },
  {
    id: "sales",
    department: "Sales",
    title: "Sales Lead",
    when: "The business wins customers by reaching out to them.",
    duties: [
      "Research target accounts: what they do, who decides, and recent news.",
      "Draft outreach in the owner's voice, from their earlier emails.",
      "Track replies and follow-ups.",
    ],
    routines: [
      {
        name: "Account research",
        prompt:
          "Research the next accounts on the target list. For each, note what they do, who decides, recent news and why the company is a fit.",
        cron: "0 9 * * 2",
      },
      {
        name: "Outreach drafts",
        prompt:
          "Draft outreach for researched accounts and follow-ups for unanswered ones, in the owner's voice, for approval before sending.",
        cron: "0 10 * * 3",
      },
    ],
    howTo: {
      name: "Sales how-to",
      description: "Account research and outreach drafts in the owner's voice.",
      body: `1. Research from public sources: the account's site, news and registers. Cite where each fact came from.
2. Learn the owner's voice from their sent emails: length, greeting, tone and sign-off.
3. Draft a short first message: one line on why them, one on the offer, one clear ask.
4. Follow up at most twice, a week apart, then stop.`,
    },
    approvals: ["Ask before sending any outreach or follow-up."],
    rules: [],
  },
  {
    id: "marketing",
    department: "Marketing",
    title: "Marketing Lead",
    when: "The business posts on social media, runs campaigns or watches competitors.",
    duties: [
      "Watch competitors: new offers, prices, posts and pages.",
      "Draft posts and campaign copy in the company's tone.",
      "Make campaign images to go with them.",
    ],
    routines: [
      {
        name: "Competitor watch",
        prompt:
          "Check the main competitors' sites and social posts for new offers, prices or messages since last week, and summarise what changed.",
        cron: "0 9 * * 1",
      },
      {
        name: "Post drafts",
        prompt:
          "Draft this week's posts from recent work and news, each with a suggested image, for approval before publishing.",
        cron: "0 11 * * 2",
      },
    ],
    howTo: {
      name: "Marketing how-to",
      description: "Competitor watch, post drafts and campaign images.",
      body: `1. Competitor watch: note only what changed, with links.
2. Posts: one idea each, written for the channel, with a clear call to action. Never invent results or reviews.
3. Images: make them with the Pictures command at the channel's shape, in the brand's colours.`,
    },
    approvals: ["Ask before publishing a post or spending on ads."],
    rules: [],
  },
  {
    id: "website",
    department: "Marketing",
    title: "Website & SEO Lead",
    when: "The business has a website. Propose it only then.",
    duties: [
      "Check the live site and the latest deploy each morning.",
      "Make one focused SEO or AI-readability improvement per run, as a commit in GitHub.",
      "Open a pull request for protected pages such as the home page and pricing.",
    ],
    routines: [
      {
        name: "Daily website upkeep",
        prompt: `Run the "${WEBSITE_UPKEEP_SKILL.name}" skill: check the live site and the latest deploy, make one improvement, and send the report.`,
        cron: "0 7 * * *",
      },
    ],
    howTo: WEBSITE_UPKEEP_SKILL,
    approvals: ["Ask before deleting a page or changing prices or legal text."],
    rules: ["Change protected pages only by pull request."],
  },
];

export function roleRecipe(id: RoleRecipeId): RoleRecipe {
  const recipe = ROLE_RECIPES.find((entry) => entry.id === id);
  if (!recipe) throw new Error(`Unknown role recipe ${id}`);
  return recipe;
}

/** Recipes whose how-to is saved as a skill in the Space; the website one is built in. */
export function recipeHasOwnSkill(recipe: RoleRecipe): boolean {
  return recipe.howTo !== WEBSITE_UPKEEP_SKILL;
}

export function recipeSkillContent(recipe: RoleRecipe): string {
  return buildSkillMd(recipe.howTo);
}

/** The routines a lead gets: the ones Chief adapted, or its recipe's own. */
export function leadRoutines(lead: Pick<AutoTeamLead, "recipe" | "routines">): AutoTeamRoutine[] {
  if (lead.routines) return lead.routines;
  return lead.recipe ? [...roleRecipe(lead.recipe).routines] : [];
}

/** The recipe library in Chief's setup instructions: one entry per recipe, routines by name. */
export function roleRecipesPromptSection(): string {
  return [
    "## Role recipes",
    "Build each lead from the recipe that fits, set its `recipe`, and adapt the recipe's routines to this business in `routines` (name, prompt, five-field cron). Change names, prompts and times to fit; drop what does not apply. Leads with no fitting recipe have no `recipe`. Routines are created paused; the owner switches each one on.",
    ...ROLE_RECIPES.map(
      (recipe) =>
        `- ${recipe.id}: ${recipe.title}. ${recipe.when} Routines: ${recipe.routines
          .map((routine) => `${routine.name} (${routine.cron})`)
          .join(", ")}. Asks first: ${recipe.approvals.join(" ")}`,
    ),
  ].join("\n");
}

/** The block a recipe adds to a lead's instructions: its how-to, rules and approvals. */
export function recipeLeadSection(recipe: RoleRecipe): string {
  return [
    `## How-to\nRead the "${recipe.howTo.name}" skill with skill_read before routine work.`,
    recipe.rules.length ? `## Standing rules\n${recipe.rules.map((r) => `- ${r}`).join("\n")}` : "",
    `## Always ask first\n${recipe.approvals.map((a) => `- ${a}`).join("\n")}`,
  ]
    .filter(Boolean)
    .join("\n\n");
}
